/**
 * Reading the partner's translation catalog — the one place that knows how a
 * `PartnerTranslationModel` becomes the `group.key → text` map the `|trans` filter
 * resolves against.
 *
 * This existed as the same eight-line loop in five places (the template drawer, the
 * list's preview drawer, the Theme tab's fragment editor, the translation field and
 * the rich-text field). The line count was the smaller problem: **the rule that makes
 * the loop correct was written down in exactly one of the five copies**, so the other
 * four were one careless edit away from silently changing what a preview shows.
 *
 * That rule:
 *
 * > **An empty string counts as present; only an absent key is missing.**
 *
 * The server resolves an existing-but-empty translation to `''` — which is how a
 * partner hides an optional block (`{% if promotion.image.first %}`). Treating empty
 * as missing would make the editor nag about values that were deliberately cleared,
 * *and* would make the preview show the key instead of the emptiness the recipient
 * gets. `undefined` is the only "missing".
 */
import { PartnerTranslationModel } from '@/typings/api/emailCenter';

/** `group.key` — the form the Twig `trans` filter and the API's `fullKey` both use. */
export function translationKey(translation: PartnerTranslationModel): string {
  return `${translation.group}.${translation.key}`;
}

/**
 * The catalog as `group.key → text` for one language, ready for `renderTwigPreview`.
 *
 * Keys with no entry for the language are **omitted** rather than mapped to `''`, so
 * `hasOwnProperty` distinguishes "the partner set this to nothing" from "the partner
 * has not translated this yet" — a distinction the missing-value indicators depend on.
 */
export function resolveTranslations(
  translations: PartnerTranslationModel[],
  language: string,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const translation of translations) {
    const text = translation.value?.[language];
    if (text !== undefined) {
      map[translationKey(translation)] = text;
    }
  }
  return map;
}

/** Every `group.key` in the catalog, sorted — for pickers and collision checks. */
export function translationKeys(
  translations: PartnerTranslationModel[],
): string[] {
  return translations.map(translationKey).sort((a, b) => a.localeCompare(b));
}

/**
 * Any wording a key has, for a list with no language of its own — the Twig source
 * editor's `{{` suggestions, which sit in a template rather than in a preview.
 *
 * A recognisable sentence in *some* language beats a bare identifier there. Callers
 * that do have a language use `resolveTranslations` instead: falling back to another
 * language's words would claim a translation that does not exist.
 */
export function anyWording(
  translation: PartnerTranslationModel,
): string | undefined {
  return Object.values(translation.value ?? {}).find(
    (text) => text !== undefined,
  );
}

/**
 * Does a row match a free-text query, over its `group.key` *and* every language's
 * wording?
 *
 * Both halves, because a reader arrives either from a template knowing the key or
 * from someone quoting the sentence they want reworded. Matched against the joined
 * `group.key` so an identifier pasted out of a template matches across the two
 * fields. An empty query matches everything.
 */
export function matchesTranslationSearch(
  translation: PartnerTranslationModel,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return [
    translationKey(translation),
    ...Object.values(translation.value ?? {}),
  ]
    .join('\n')
    .toLowerCase()
    .includes(needle);
}

/** Every group the partner already uses, sorted — the choices for a new key. */
export function translationGroups(
  translations: PartnerTranslationModel[],
): string[] {
  return [...new Set(translations.map((each) => each.group))].sort((a, b) =>
    a.localeCompare(b),
  );
}
